import type { ConstructItem, ConstructLevel } from '../../types/gotypes'

// CROSSED_NET_LABEL_JOINER separates the level labels of a crossed-net code.
// ASCII on purpose — mirrors goapi/pkg/constructs/crossednet.go's
// CrossedNetLabelJoiner: the label is written into a .met file Carbon reads
// back, and a typographic "×" has burned us in variable labels before.
export const CROSSED_NET_LABEL_JOINER = ' x '

// expandCrossedNet turns the levels of a crossed net into the flat code list
// that is actually saved (design doc Decision 5: DefNet is not used). It is
// the TypeScript twin of Go's ExpandCrossedNet (crossednet.go) — same table,
// same order, kept honest by a shared test table (crossednet_test.go /
// expandCrossedNet.test.ts) because a wizard that previews a different code
// order from the one Go saves is a silently wrong variable.
//
// It walks the levels row-major — the LAST level varies fastest — so
// gender x age reads "Male x 18-34, Male x 35-54, …". Codes are 1-based in
// that order, labels are joined with CROSSED_NET_LABEL_JOINER and filters
// ANDed with every operand bracketed, "(gender(1))&(age(1))": Carbon binds &
// tighter than |, so a level filter such as "age(1)|age(2)" joined bare would
// read gender(1)&age(1) OR age(2).
export function expandCrossedNet(levels: ConstructLevel[]): ConstructItem[] {
  const items: ConstructItem[] = []
  if (!levels || levels.length === 0) return items

  let total = 1
  for (const level of levels) {
    total *= level.items.length
  }
  if (total === 0) return items

  const idx = new Array(levels.length).fill(0)
  for (let n = 0; n < total; n++) {
    const labels: string[] = []
    const filters: string[] = []
    for (let i = 0; i < levels.length; i++) {
      const item = levels[i].items[idx[i]]
      labels.push(item.label)
      filters.push(item.filter)
    }
    items.push({
      code: n + 1,
      label: labels.join(CROSSED_NET_LABEL_JOINER),
      filter: andCrossedNetFilters(filters),
    })
    // Odometer, least significant (last) level first.
    for (let i = levels.length - 1; i >= 0; i--) {
      idx[i]++
      if (idx[i] < levels[i].items.length) break
      idx[i] = 0
    }
  }
  return items
}

// andCrossedNetFilters ANDs the level filters of one crossed-net code: one
// level is its filter as is, two or more are each bracketed (as andFilters in
// utils/filter.ts does for two). Mirrors Go's andCrossedNetFilters.
function andCrossedNetFilters(filters: string[]): string {
  if (filters.length === 1) return filters[0]
  return filters.map(f => `(${f})`).join('&')
}

// itemsMatchCrossedNet reports whether items is exactly what the levels
// expand to. A crossed net's codes are generated, never hand-edited:
// anything else would reopen in the wizard as something other than what was
// saved. Mirrors Go's ItemsMatchCrossedNet.
export function itemsMatchCrossedNet(items: ConstructItem[], levels: ConstructLevel[]): boolean {
  const want = expandCrossedNet(levels)
  if (items.length !== want.length) return false
  for (let i = 0; i < want.length; i++) {
    const a = items[i]
    const b = want[i]
    if (a.code !== b.code || a.label !== b.label || a.filter !== b.filter) return false
  }
  return true
}
